import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { setExceptionHandler, getLoaderBtn, getModalSwitch, handleReloadShellConfig } from '@app/store/actions';
import { ModalActionsComponent, PasslySelect, PasslyTextField } from '@app/components';
import { FormControl } from '@material-ui/core';
import { hasPermission, onPressEnterModal, genericErrorString } from '@app/helpers';
import { ManageSystem } from '@app/store/constants';
import { policiesAuthService, organizationsService } from '@app/services';
import { IPolicyAuth } from '@app/models';
import { ImageUpload } from '@app/pages/directoryManager/imageUpload/imageUpload.component';

export interface IReceivedData {
    orgId: string;
    orgName: string;
    principalNameSuffix: string | null;
    authPolicyId: string;
    handleRefresh: Function;
    imageUrl: string;
}

export const EditOrganizationDialog = (props: IReceivedData) => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [orgName, setOrgName] = useState('');
    const [orgNameInvalid, setOrgNameInvalid] = useState(false);
    const [disableAuthPolicy, setDisableAuthPolicy] = useState(false);
    const [authPolicies, setAuthPolicies] = useState<IPolicyAuth[] | undefined>(undefined);
    const [principalNameSuffix, setPrincipalNameSuffix] = useState('');
    const [authPolicyId, setAuthPolicyId] = useState('');
    const [formValid, setFormValid] = useState(true);
    const [brandImageUrl, setBrandImageUrl] = useState(props.imageUrl);

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        setOrgName(props.orgName);

        if (props.principalNameSuffix !== null) {
            setPrincipalNameSuffix(props.principalNameSuffix);
        }

        if (hasPermission([ManageSystem])) {
            policiesAuthService.getAuthPolicies(handleError, handleSuccessAuthPolicies);
        } else {
            setDisableAuthPolicy(true);
        }

        setAuthPolicyId(props.authPolicyId);
    }, []);

    useEffect(() => {
        if (orgNameInvalid) {
            setFormValid(false);
        } else {
            setFormValid(true);
        }
    }, [orgNameInvalid]);

    const handleSuccessAuthPolicies = (response: any) => {
        setAuthPolicies(response.data.value);
    };

    const onFormChange = (e: any) => {
        const { name, value } = e.target;

        switch (name) {
            case 'orgName':
                setOrgName(value.trim());
                break;
            case 'principalNameSuffix':
                setPrincipalNameSuffix(value.trim());
                break;
            case 'authPolicyId':
                setAuthPolicyId(value);
                break;
            default:
                break;
        }
    };

    const onOrgNameChange = (e: any) => {
        setErrorMessage('');
        onFormChange(e);

        const orgNameChosen = e.target.value.trim();

        if (orgNameChosen === '') {
            setOrgNameInvalid(true);
        } else {
            setOrgNameInvalid(false);
        }
    };

    const handleError = (error: any) => {
        setErrorMessage(genericErrorString(error));

        dispatch(getLoaderBtn(false));
    };

    const handleOnImageChange = (newImageUrl: string) => {
        setBrandImageUrl(newImageUrl);
    };

    const handleCancel = () => {
        dispatch(getModalSwitch(false));
    };

    const handleSuccess = () => {
        dispatch<any>(handleReloadShellConfig());
        dispatch(getLoaderBtn(false));
        dispatch(getModalSwitch(false));
        props.handleRefresh(true);
    };

    const handleSaveBtn = () => {
        dispatch(getLoaderBtn(true));

        let jsonObject: any = {};
        jsonObject.name = orgName;
        jsonObject.principalNameSuffix = principalNameSuffix;
        jsonObject.defaultAuthPolicyId = authPolicyId;
        jsonObject.brandImageUrl = brandImageUrl;

        organizationsService.editOrgBasic(props.orgId, jsonObject, handleError, handleSuccess);
    };

    return (
        <>
            <form className='modal__form' method='post' tabIndex={0} onKeyPress={onPressEnterModal(handleSaveBtn, !formValid)}>
                <FormControl>
                    <PasslyTextField
                        name='orgName'
                        label={'ORG_NAME'}
                        value={orgName}
                        onChange={onOrgNameChange}
                        id='orgName-input'
                        autoFocus
                        required
                        error={orgNameInvalid}
                        helperText={orgNameInvalid && translation('REQUIRED_FIELD')}
                        fullWidth
                    />
                </FormControl>
                <FormControl>
                    <PasslyTextField
                        name='principalNameSuffix'
                        label={'ORG_SUFFIX'}
                        value={principalNameSuffix}
                        onChange={onFormChange}
                        id='principalNameSuffix-input'
                        fullWidth
                    />
                </FormControl>
                <FormControl>
                    <PasslySelect
                        name='authPolicyId'
                        value={authPolicyId}
                        onChange={onFormChange}
                        id='authPolicyId-input'
                        label={'ORG_POLICY'}
                        disabled={disableAuthPolicy}
                        optionLabel='DisplayName'
                        optionValue='Id'
                        options={authPolicies}
                    />
                </FormControl>
                <FormControl>
                    <ImageUpload
                        width={150}
                        height={50}
                        label={'BRAND_IMAGE'}
                        currentImageUrl={brandImageUrl}
                        onImageChange={handleOnImageChange}
                        onFileUpload={organizationsService.uploadOrganizationLogo}
                    />
                </FormControl>
            </form>

            {errorMessage && <div className='modal__error'>{errorMessage}</div>}
            <ModalActionsComponent
                primaryBtn={translation('SAVE_CHANGES')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSaveBtn}
                onClickSecondary={handleCancel}
                disabled={!formValid}
                id='editOrg'
            />
        </>
    );
};
